import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Toolbar, useTheme } from '@/shared/ui/mui';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { dashTokens } from '@/shared/styles/dashboard.tokens';
import { useNavigationIdentity } from '../navigation/hooks';
import { SideMenu } from '../side-menu';
import { DashboardAppBar } from './components/DashboardAppBar';
import { DashboardShellProvider } from './DashboardShellContext';

const SIDEBAR_WIDTH = 280;

export const DashboardShellLayout: FC = () => {
  const theme = useTheme();
  const { isMobile, isDesktop } = useResponsive();
  const identity = useNavigationIdentity();
  const location = useLocation();
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);

  // Auto-close mobile drawer on route change
  useEffect(() => {
    if (isMobile) {
      setIsSideMenuOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleMenuToggle = () => {
    setIsSideMenuOpen(prev => !prev);
  };

  return (
    <DashboardShellProvider>
      <Box sx={{ display: 'flex' }}>
        <DashboardAppBar
          onMenuToggle={handleMenuToggle}
          user={identity.user}
          showMenuButton={!isDesktop}
          shops={identity.shops}
          currentShopSlug={identity.shopSlug}
        />

        <SideMenu
          open={isDesktop || isSideMenuOpen}
          onClose={() => setIsSideMenuOpen(false)}
          userType={identity.userType}
          variant={isDesktop ? 'permanent' : 'temporary'}
          menuItems={identity.menuItems}
          commonItems={identity.commonItems}
          user={identity.user}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: isDesktop ? `calc(100% - ${SIDEBAR_WIDTH}px)` : '100%',
            minHeight: '100vh',
            bgcolor: dashTokens.bgPage,
            ml: isDesktop ? `${SIDEBAR_WIDTH}px` : 0,
            transition: theme.transitions.create('margin-left', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Toolbar />
          {/* Extra spacer for promo ribbon above navbar */}
          <Box sx={{ height: 'var(--promo-offset, 0px)', transition: 'height 0.3s ease' }} />
          <Outlet />
        </Box>
      </Box>
    </DashboardShellProvider>
  );
};
